import { BaseElement } from 'base';
import { runAction } from 'run-action';

const LABEL_RESTING = 't-control-md-line';
const LABEL_FLOATED = 't-control-sm-line';
const REANNOUNCE = '\u00A0';

class TextField extends BaseElement {
  #action = null;
  #handlers = new Set();
  #control = null;
  #button = null;

  get control() {
    return this.#control;
  }

  get button() {
    return this.#button;
  }

  onConnect() {
    this.box = this.q('[data-text-field-box]');
    this.#control = this.q('[data-text-field-control]');
    this.#button = this.q('[data-text-field-button] action-button');
    this.label = this.q('[data-text-field-label]');
    this.message = this.q('.text-field__message');
    this.messageText = this.message?.lastElementChild;
    this.successText = this.q('[data-text-field-success]');
    this.scrollArea = this.q('.text-field__scroll-area');
    this.hintText = this.dataset.hint ?? '';
    this.hintHidden = this.hintText === '';

    if (!this.control) {
      console.error('[text-field] no [data-text-field-control] in', this);
      return;
    }

    this.listen(this.control, 'input', this.syncLabel);
    this.listen(this.control, 'focus', this.syncLabel);
    this.listen(this.control, 'blur', this.syncLabel);
    this.listen(this.control, 'change', () =>
      this.#emit({ id: this.control?.id ?? '', value: this.value })
    );
    if (this.control.form)
      this.listen(this.control.form, 'reset', this.afterReset);
    this.cleanup(() => clearTimeout(this.resetTimer));
    this.cleanup(() => cancelAnimationFrame(this.successFrame));

    if (this.box) this.listen(this.box, 'click', this.onBoxClick);
    const slot = this.q('[data-text-field-button]');
    if (slot) this.listen(slot, 'click', this.#onSlotClick);

    if (!this.label || !this.label.textContent.trim())
      console.error(
        '[text-field] label is empty — the field has no accessible name:',
        this
      );

    this.syncLabel();
  }

  onDisconnect() {
    this.#handlers.clear();
  }

  focusControl() {
    this.control?.focus();
  }

  run(action) {
    this.#action = action;
    return this;
  }

  onChange(handler) {
    this.#handlers.add(handler);
    return () => this.#handlers.delete(handler);
  }

  clear() {
    this.control.value = '';
    this.scrollArea?.refresh?.();
    this.syncLabel();
    this.clearError();
  }

  setError(message) {
    const repeated =
      this.getAttribute('data-state') === 'error' &&
      this.messageText?.textContent === message;
    this.setAttribute('data-state', 'error');
    if (this.messageText)
      this.messageText.textContent = repeated
        ? `${message}${REANNOUNCE}`
        : message;
    if (this.message) this.message.hidden = false;
    this.control?.setAttribute('aria-invalid', 'true');
  }

  clearError() {
    this.removeAttribute('data-state');
    if (this.messageText) this.messageText.textContent = this.hintText;
    if (this.message) this.message.hidden = this.hintHidden;
    this.control?.removeAttribute('aria-invalid');
  }

  setSuccess(message) {
    if (!this.control || !this.#canConfirm()) return;
    this.clearError();
    this.setAttribute('data-state', 'success');
    if (this.successText) {
      cancelAnimationFrame(this.successFrame);
      this.successText.textContent = '';
      this.successText.hidden = false;
      this.successFrame = requestAnimationFrame(() => {
        this.successFrame = requestAnimationFrame(() => {
          if (this.successText) this.successText.textContent = message;
        });
      });
    }
    this.control.value = '';
    this.scrollArea?.refresh?.();
    this.control.readOnly = true;
    this.#describe(true);
    this.syncLabel();
    this.button?.setIconState?.('confirm');
  }

  clearSuccess() {
    if (!this.control) return;
    cancelAnimationFrame(this.successFrame);
    if (this.getAttribute('data-state') === 'success')
      this.removeAttribute('data-state');
    if (this.successText) {
      this.successText.textContent = '';
      this.successText.hidden = true;
    }
    this.control.readOnly = false;
    this.#describe(false);
    this.syncLabel();
    this.button?.setIconState?.('default');
  }

  #canConfirm() {
    if (
      !this.q('[data-text-field-button]') ||
      this.q("[data-text-field-button] [data-icon-for='confirm']")
    )
      return true;
    console.error(
      '[text-field] the success state needs a button rendered with button_icon_confirm:',
      this
    );
    return false;
  }

  setDisabled(isDisabled) {
    if (!this.control) return;
    this.control.disabled = isDisabled;
    this.button?.setDisabled?.(isDisabled);
  }

  #describe(withSuccess) {
    const ids = [this.message?.id];
    if (withSuccess) ids.unshift(this.successText?.id);
    const value = ids.filter(Boolean).join(' ');
    if (value) this.control.setAttribute('aria-describedby', value);
  }

  get value() {
    return this.control?.value ?? '';
  }

  focus(options) {
    this.control?.focus(options);
  }

  sanitize(clean) {
    if (!this.control) return;
    const apply = event => {
      if (event.isComposing) return;
      const { value } = this.control;
      const next = clean(value);
      if (next === value) return;
      const caret = this.control.selectionStart;
      this.control.value = next;
      if (caret != null) {
        const position = clean(value.slice(0, caret)).length;
        this.control.setSelectionRange(position, position);
      }
      this.syncLabel();
    };
    this.listen(this.control, 'input', apply);
    this.listen(this.control, 'compositionend', apply);
  }

  syncLabel() {
    if (!this.label) return;
    const floated =
      this.control.matches(':focus') ||
      !this.control.matches(':placeholder-shown');
    this.label.classList.toggle(LABEL_RESTING, !floated);
    this.label.classList.toggle(LABEL_FLOATED, floated);
  }

  afterReset() {
    clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => {
      this.syncLabel();
      this.scrollArea?.refresh?.();
    }, 0);
  }

  onBoxClick(event) {
    if (event.target.closest('a, button, input, select, textarea')) return;
    this.control.focus();
  }

  #onSlotClick() {
    runAction(
      this,
      this.#action,
      { id: this.control?.id ?? '', value: this.value },
      'text-field'
    );
  }

  #emit(detail) {
    for (const handler of [...this.#handlers]) {
      try {
        handler(detail);
      } catch (error) {
        console.error('[text-field] change handler failed:', error);
      }
    }
  }
}

if (!customElements.get('text-field'))
  customElements.define('text-field', TextField);
